/**
 * WebsiteLatenBouwenPage — Publieke landing voor bezoekers zonder website.
 *
 * Bereikbaar via /website-laten-bouwen. Gelinkt vanaf de homepage hero
 * ("Nog geen website? Laat er één voor u bouwen"). Toont hoe IMPERIA AI
 * een complete website + marketing bouwt en linkt door naar Managed AI checkout
 * of naar een offerte-formulier.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowRight, Check, Sparkles, Globe, MessageCircle, Rocket, Shield } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const HIGHLIGHTS = [
  'Volledige website op maat — geen template-look',
  'AI-marketing meteen ingebakken: SEO, Google Bedrijfsprofiel, backlinks',
  'Live in 14 dagen — u ziet dagelijks de voortgang',
  'Maandelijks opzegbaar na Launch Month',
];

const STEPS = [
  { icon: MessageCircle, title: '1. Vertel ons wat u wilt', desc: 'Na registratie krijgt u een AI-chat waar u in gewone taal beschrijft welke website u nodig heeft. Onze AI stelt de goede vragen.' },
  { icon: Sparkles, title: '2. Onze AI + team bouwt', desc: 'AI genereert eerste ontwerp binnen 48u. Ons team verfijnt, schrijft SEO-content en zorgt dat alles perfect werkt.' },
  { icon: Rocket, title: '3. Live in 14 dagen', desc: 'Uw nieuwe website gaat live én we lanceren direct de marketing-campagnes zodat u klanten binnenkrijgt.' },
];

export default function WebsiteLatenBouwenPage() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', description: '' });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.name) { toast.error('Vul minimaal naam en e-mail in'); return; }
    setBusy(true);
    try {
      await axios.post(`${BACKEND}/api/public/website-request/lead`, {
        ...form,
        source: 'website-laten-bouwen-landing',
      });
      toast.success('Bedankt! We nemen binnen 1 werkdag contact op.');
      setForm({ name: '', email: '', phone: '', company: '', description: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Er ging iets mis — probeer opnieuw');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="website-laten-bouwen-page">
        {/* Hero */}
        <section className="bg-gradient-to-br from-[#0f172a] via-[#1e1b4b] to-[#0f172a] text-white py-20 lg:py-28">
          <div className="max-w-6xl mx-auto px-5 lg:px-10">
            <div className="grid lg:grid-cols-2 gap-14 items-center">
              <div>
                <span className="inline-block bg-[#D4A017]/15 border border-[#D4A017]/30 text-[#D4A017] text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider mb-5">
                  Voor bedrijven zonder website
                </span>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] mb-6" style={{ fontFamily: "'Sora', sans-serif" }}>
                  Geen website? <span className="text-[#D4A017]">Wij bouwen 'm voor u — met AI-marketing erin.</span>
                </h1>
                <p className="text-white/75 text-base md:text-lg leading-relaxed mb-7 max-w-xl">
                  Vertel ons in gewone taal wat u wilt. Onze AI + team bouwt binnen 14 dagen een compleet nieuwe website mét SEO,
                  Google Bedrijfsprofiel-koppeling, backlinks en klantenwerving — direct actief vanaf dag 1.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link
                    to="/register?package=imperium_beheer_launch"
                    data-testid="cta-managed-signup"
                    className="bg-[#D4A017] hover:bg-[#e6b52a] text-[#0f172a] font-bold px-6 py-3.5 rounded-xl inline-flex items-center gap-2 transition-colors"
                  >
                    Start Managed AI — €995 eerste maand <ArrowRight size={16} />
                  </Link>
                  <a
                    href="#offerte"
                    data-testid="cta-offerte"
                    className="bg-white/5 hover:bg-white/10 border border-white/15 text-white font-semibold px-6 py-3.5 rounded-xl inline-flex items-center gap-2 transition-colors"
                  >
                    Eerst offerte aanvragen
                  </a>
                </div>
                <div className="grid sm:grid-cols-2 gap-2 mt-8 max-w-lg">
                  {HIGHLIGHTS.map((h) => (
                    <div key={h} className="flex items-start gap-2 text-white/80 text-sm">
                      <Check size={16} className="text-[#D4A017] shrink-0 mt-0.5" />
                      <span>{h}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative">
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 shadow-2xl">
                  <div className="flex items-center gap-2 text-[10px] text-white/60 uppercase tracking-wider font-bold mb-4">
                    <span className="w-2 h-2 bg-[#D4A017] rounded-full animate-pulse" /> AI Website Chat · Live voorbeeld
                  </div>
                  <div className="space-y-3 text-sm">
                    <div className="bg-white/10 rounded-xl rounded-tl-sm p-3.5">
                      <div className="text-[10px] text-white/50 mb-1 font-bold">U</div>
                      <div className="text-white/90">Ik heb een kapsalon in Zwolle, 3 medewerkers. Wil online afspraken kunnen aannemen en beter vindbaar zijn.</div>
                    </div>
                    <div className="bg-[#D4A017]/15 border-l-2 border-[#D4A017] rounded-xl rounded-tl-sm p-3.5">
                      <div className="text-[10px] text-[#D4A017] mb-1 font-bold">IMPERIA AI</div>
                      <div className="text-white/90">Top! Ik bouw een moderne website met online afsprakenkalender, prijslijst en review-integratie. Ik registreer u meteen in 80+ NL directories en optimaliseer Google Bedrijfsprofiel voor "kapper Zwolle". Live in 14 dagen. Zullen we starten?</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Steps */}
        <section className="bg-white py-20 lg:py-24">
          <div className="max-w-6xl mx-auto px-5 lg:px-10">
            <h2 className="text-3xl lg:text-4xl font-extrabold text-[#0f172a] mb-14 text-center" style={{ fontFamily: "'Sora', sans-serif" }}>
              Van idee naar live website in <span className="text-[#D4A017]">14 dagen</span>
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {STEPS.map((s, i) => (
                <div key={i} className="bg-slate-50 border border-slate-200 rounded-2xl p-7">
                  <div className="w-11 h-11 bg-[#0f172a] rounded-xl flex items-center justify-center mb-4">
                    <s.icon className="w-5 h-5 text-[#D4A017]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0f172a] mb-2" style={{ fontFamily: "'Sora', sans-serif" }}>{s.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Offerte formulier */}
        <section id="offerte" className="bg-slate-50 py-20 lg:py-24">
          <div className="max-w-3xl mx-auto px-5 lg:px-10">
            <div className="text-center mb-10">
              <h2 className="text-3xl lg:text-4xl font-extrabold text-[#0f172a] mb-4" style={{ fontFamily: "'Sora', sans-serif" }}>
                Vraag vrijblijvend offerte aan
              </h2>
              <p className="text-slate-600">Binnen 1 werkdag krijgt u een persoonlijk voorstel op maat. Geen verplichtingen.</p>
            </div>
            <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-7 shadow-sm" data-testid="offerte-form">
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">Uw naam *</label>
                  <input data-testid="offerte-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="Jan de Vries" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">E-mail *</label>
                  <input data-testid="offerte-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} placeholder="jan@bedrijf.nl" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">Telefoon</label>
                  <input data-testid="offerte-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} placeholder="06 12 34 56 78" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">Bedrijfsnaam</label>
                  <input data-testid="offerte-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className={inputCls} placeholder="Bedrijf BV" />
                </div>
              </div>
              <div className="mb-5">
                <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">Vertel kort wat u nodig heeft</label>
                <textarea data-testid="offerte-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} className={inputCls} placeholder="Bijv. kapsalon in Zwolle met afsprakenkalender en Instagram-koppeling" />
              </div>
              <button type="submit" disabled={busy} data-testid="offerte-submit"
                className="w-full bg-[#0f172a] hover:bg-[#1e293b] disabled:opacity-50 text-white font-bold py-3.5 rounded-xl inline-flex items-center justify-center gap-2 transition-colors">
                {busy ? 'Bezig…' : <>Verstuur aanvraag <ArrowRight size={16} /></>}
              </button>
              <p className="text-slate-400 text-xs text-center mt-3">
                <Shield size={12} className="inline mr-1" /> Uw gegevens gaan nergens anders heen. We bellen alleen om uw wensen te bespreken.
              </p>
            </form>
          </div>
        </section>
      </div>
  );
}

const inputCls = 'w-full bg-slate-50 border border-slate-200 focus:border-[#D4A017] focus:bg-white rounded-lg px-3.5 py-2.5 text-sm text-[#0f172a] outline-none transition-colors';
